import {
  ApiCipSipRecord,
  ApiProcessChemical,
  ApiProcessDetail,
  ApiProcessWaste,
  ChemicalRecord,
  CipSipFormValues,
  CipSipRecord,
  MassUnit,
  ProcessDetailLookups,
  ProcessDetailViewModel,
  TransportRoute,
  WasteRecord
} from "../types/processDetail.types";
import { MASS_UNIT_MAP } from "../../shared/constants/measurementUnits.constants";

// Single source of truth lives at the screen root — re-exported here so existing
// consumers (ProcessSubsections, tests) keep importing it from this module.
export { MASS_UNIT_MAP };

export const massUnitToId = (unit: MassUnit): number => (unit === "ton" ? 2 : 1);

const isPopulatedChemical = (item: ApiProcessChemical | string): item is ApiProcessChemical =>
  typeof item === "object" && item !== null;

const isPopulatedWaste = (item: ApiProcessWaste | string): item is ApiProcessWaste =>
  typeof item === "object" && item !== null;

export const mapApiTransportToRoute = (
  transport: ApiProcessChemical["transportationTypes"][number]
): TransportRoute => ({
  id: transport._id,
  type: String(transport.transportType),
  distance: transport.distance
});

export const mapRouteToApiTransport = (route: TransportRoute): { transportType: number; distance: number } => ({
  transportType: Number(route.type),
  distance: route.distance === "" ? 0 : Number(route.distance)
});

export const mapApiChemicalToRecord = (chemical: ApiProcessChemical): ChemicalRecord => ({
  id: chemical._id,
  name: chemical.chemicalName ?? "",
  function: String(chemical.chemicalType),
  amount: chemical.amount,
  unit: "kg",
  hasTransportRoute: chemical.isTransportationDetailsAvailable,
  routes: (chemical.transportationTypes ?? []).map(mapApiTransportToRoute)
});

export const mapRecordToApiChemical = (
  record: Pick<ChemicalRecord, "name" | "function" | "amount" | "hasTransportRoute">
): {
  chemicalName: string;
  chemicalType: number;
  amount: string;
  isTransportationDetailsAvailable: boolean;
} => ({
  chemicalName: record.name,
  chemicalType: Number(record.function || 0),
  amount: String(record.amount === "" ? 0 : record.amount),
  isTransportationDetailsAvailable: record.hasTransportRoute
});

export const mapApiWasteToRecord = (
  waste: ApiProcessWaste,
  lookups: Pick<ProcessDetailLookups, "ingredientNameMap">
): WasteRecord => ({
  id: waste._id,
  streamName: waste.managementName,
  type: "",
  managementType: String(waste.managementTypeId),
  distribution: waste.sideStreamsDistribution,
  distributionUnit: MASS_UNIT_MAP[waste.unit] ?? "kg",
  ingredientName: waste.ingredientId ? lookups.ingredientNameMap[waste.ingredientId] ?? "" : "",
  moisture: waste.moistureContent ?? ""
});

/** Deterministic id for a CIP/SIP record — the API never returns a client-side `id`. */
export const cipSipRecordId = (record: ApiCipSipRecord, index: number): string => record._id ?? `cipsip-${index}`;

export const mapApiCipSipToRecord = (record: ApiCipSipRecord, index: number): CipSipRecord => ({
  ...record,
  id: cipSipRecordId(record, index)
});

export const mapCipSipFormValuesToApi = (values: CipSipFormValues): ApiCipSipRecord => ({ ...values });

export const mapProcessDetail = (
  detail: ApiProcessDetail | undefined,
  lookups: ProcessDetailLookups
): ProcessDetailViewModel => {
  if (!detail) {
    return { chemicals: [], wastes: [], cipSipRecords: [] };
  }

  const chemicals = (detail.chemical ?? []).filter(isPopulatedChemical).map(mapApiChemicalToRecord);
  const wastes = (detail.wasteManagements ?? [])
    .filter(isPopulatedWaste)
    .map(waste => mapApiWasteToRecord(waste, lookups));
  const cipSipRecords = (detail.cipSip ?? []).map(mapApiCipSipToRecord);

  return { chemicals, wastes, cipSipRecords };
};

export const buildLookupMap = (options: Array<{ value: string | number; text: string }> | undefined) => {
  const map: Record<string, string> = {};
  (options ?? []).forEach(o => {
    map[String(o.value)] = o.text;
  });
  return map;
};
